import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { LoadingButton } from '../../common/LoadingButton';
import ExtractedImageCard from './ExtractedImageCard';
import ImageComparisonControls from './ImageComparisonControls';
import type { DocumentImagesState } from '@hooks/useDocumentImages';
import type { ImageComparisonState } from '@hooks/useImageComparison';

interface ExtractedImagesPanelProps {
  imageState: DocumentImagesState;
  comparison: ImageComparisonState;
}

const ExtractedImagesPanel: React.FC<ExtractedImagesPanelProps> = ({ imageState, comparison }) => {
  const { t } = useTranslation();
  const { images } = imageState;

  return (
    <div className="mt-8">
      {/* The comparison controls sit inside this row rather than below it,
          matching the pre-port DOM. */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className="revy-cjk text-base font-medium leading-[1.5]">
          {t('documents.extractedImagesTitle')}{' '}
          {images.length > 0 && t('documents.itemCount', { count: images.length })}
        </h3>

        <div className="flex flex-col items-end gap-2">
          {images.length > 0 && (
            <>
              <LoadingButton
                loading={imageState.analyzingImages}
                loadingText={t('documents.aiAnalyzing')}
                onClick={imageState.analyzeAllImages}
              >
                <Sparkles strokeWidth={1.9} />
                <span className="revy-cjk">{t('documents.analyzeAllImages')}</span>
              </LoadingButton>
              <div className="flex items-center gap-1">
                <Checkbox
                  id="force-reanalyze"
                  checked={imageState.forceReanalyze}
                  onCheckedChange={(next) => imageState.setForceReanalyze(next === true)}
                />
                <Label htmlFor="force-reanalyze" className="revy-cjk cursor-pointer text-sm font-normal leading-[1.5]">
                  {t('documents.forceReanalyze')}
                </Label>
              </div>
            </>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={imageState.reExtractStandard}
              disabled={imageState.reExtracting}
            >
              <span className="revy-cjk">
                {imageState.reExtracting ? t('documents.extracting') : t('documents.reExtractImages')}
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={imageState.reExtractBlueprint}
              disabled={imageState.reExtracting}
            >
              <span className="revy-cjk">
                {imageState.reExtracting
                  ? t('documents.extracting')
                  : t('documents.reExtractAsBlueprint')}
              </span>
            </Button>
          </div>
        </div>

        {images.length >= 2 && <ImageComparisonControls comparison={comparison} />}
      </div>

      {imageState.imagesLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : images.length > 0 ? (
        <div className="mt-4 grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
          {images.map((image) => (
            <ExtractedImageCard
              key={image.id}
              image={image}
              blobUrl={imageState.imageBlobUrls[image.id]}
              failed={imageState.failedImageIds.has(image.id)}
              isSelected={comparison.selectedImages.includes(image.id)}
              selectionIndex={comparison.selectedImages.indexOf(image.id)}
              analyzing={imageState.analyzingImageId === image.id}
              onToggleSelection={comparison.toggleImage}
              onAnalyze={imageState.analyzeImage}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed bg-muted p-6 text-center">
          <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
            {t('documents.noExtractedImages')}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExtractedImagesPanel;
